import { useEffect, useState } from "react";
import axios from "axios";
import { Flame, MoonStar, BookOpen } from "lucide-react";
import Reveal from "@/components/Reveal";
import { API } from "@/lib/site";

const fmtFull = (v) =>
  v
    ? new Intl.DateTimeFormat("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        timeZone: "America/New_York",
      }).format(new Date(v))
    : null;

function useCountdown(target) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);
  if (!target) return null;
  const diff = new Date(target).getTime() - now;
  if (diff <= 0) return null;
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  return d > 0 ? `${d}d ${h}h ${m}m` : `${h}h ${m}m`;
}

export default function ShabbatWidget() {
  const [d, setD] = useState(null);
  const countdown = useCountdown(d?.candle_lighting);

  useEffect(() => {
    axios.get(`${API}/shabbat`).then((r) => setD(r.data)).catch(() => {});
  }, []);

  if (!d) return null;

  return (
    <section data-testid="shabbat-widget" className="border-b border-white/10 py-20 md:py-28 relative overflow-hidden">
      <span
        aria-hidden
        className="pointer-events-none select-none absolute -top-8 left-0 font-syne font-extrabold text-[24vw] md:text-[16vw] leading-none text-gold-outline opacity-15"
      >
        שבת
      </span>
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
        <Reveal className="flex flex-col lg:flex-row lg:items-end justify-between gap-10">
          <div className="max-w-xl">
            <p className="text-[10px] md:text-xs tracking-[0.35em] uppercase text-gold mb-4">
              This Week's Shabbat · {d.location || "West Palm Beach"}
            </p>
            <h2 className="font-syne font-bold uppercase tracking-tighter leading-[0.95] text-3xl md:text-5xl">
              {d.parasha ? d.parasha.replace("Parashat ", "Parashat\u00A0") : "Shabbat Shalom"}
            </h2>
            <p className="mt-4 font-cormorant italic text-xl text-foreground/60" data-testid="shabbat-widget-hebrew-date">
              {d.hebrew_date}
            </p>
            {countdown && (
              <p className="mt-3 text-xs tracking-[0.25em] uppercase text-foreground/50" data-testid="shabbat-countdown">
                Candle lighting in <span className="text-gold">{countdown}</span>
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/10 border border-white/10 w-full lg:w-auto">
            <div className="bg-ink p-6 md:p-8" data-testid="shabbat-candles-card">
              <Flame size={20} className="text-gold mb-4" />
              <p className="text-[10px] tracking-[0.3em] uppercase text-foreground/40 mb-2">Candle Lighting</p>
              <p className="font-syne font-bold text-lg md:text-xl leading-snug">{fmtFull(d.candle_lighting) || "See calendar"}</p>
            </div>
            <div className="bg-ink p-6 md:p-8" data-testid="shabbat-havdalah-card">
              <MoonStar size={20} className="text-gold mb-4" />
              <p className="text-[10px] tracking-[0.3em] uppercase text-foreground/40 mb-2">Havdalah</p>
              <p className="font-syne font-bold text-lg md:text-xl leading-snug">{fmtFull(d.havdalah) || "See calendar"}</p>
            </div>
            <a
              href="/live"
              data-testid="shabbat-livestream-card"
              className="bg-ink p-6 md:p-8 group hover:bg-surface transition-colors duration-500"
            >
              <BookOpen size={20} className="text-gold mb-4" />
              <p className="text-[10px] tracking-[0.3em] uppercase text-foreground/40 mb-2">Join Us</p>
              <p className="font-syne font-bold text-lg md:text-xl leading-snug group-hover:text-gold transition-colors duration-500">
                In person or livestream
              </p>
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
